<script setup lang="ts">
import useVuelidate from "@vuelidate/core";
import { helpers, required, email as emailValidator } from "@vuelidate/validators";
import { useValidationHelpers, createLeeftijdRangeValidator } from "@/helpers/validation.helper";
import BestelVraagInput from "./BestelVraagInput.vue";
import type { VolwasseneItem } from "@/stores/sportactiviteit-bestel.store";
import { useSportactiviteitBestelStore } from "@/stores/sportactiviteit-bestel.store";

const props = defineProps<{
    item: VolwasseneItem;
    index: number;
}>();

const emits = defineEmits<{ 
    (e: "remove"): void 
}>();

const store = useSportactiviteitBestelStore();

const emailUniek = helpers.withMessage(
    "Dit e-mailadres is al opgegeven voor een andere deelnemer.",
    (val: string) => {
        if (!val) return true;
        return store.boeking.Volwassenen
            .filter((_, i) => i !== props.index)
            .every((v) => v.Emailadres.toLowerCase() !== val.toLowerCase());
    },
);

const rules = {
    item: {
        Voornaam: { required: helpers.withMessage("Dit veld is verplicht", required) },
        Achternaam: { required: helpers.withMessage("Dit veld is verplicht", required) },
        Leeftijd: {
            required: helpers.withMessage("Dit veld is verplicht", required),
            leeftijdRange: createLeeftijdRangeValidator(
                () => store.activiteit?.MinLeeftijd ?? 0,
                () => store.activiteit?.MaxLeeftijd ?? 999,
            ),
        },
        Geslacht: { required: helpers.withMessage("Dit veld is verplicht", required) },
        Emailadres: {
            required: helpers.withMessage("Dit veld is verplicht", required),
            email: helpers.withMessage("Dit is geen geldig e-mailadres", emailValidator),
            emailUniek,
        },
    },
};

const v$ = useVuelidate(rules, props);
const { getFieldErrors } = useValidationHelpers(v$);
</script>

<template>
    <div class="mb-3">
        <h2 class="payment-title h4 mb-2 mb-md-1">
            Gegevens vriend(in) {{ index + 1 }}
            <a href="#" class="font-size-small ml-1 text-decoration-none" @click.prevent="emits('remove')" aria-label="Verwijder vriend(in)">
                <span class="icon-delete" aria-hidden="true"></span>
            </a>
        </h2>
        <div class="row">
            <!-- Voornaam -->
            <div class="col-sm-6" :class="{ 'has-error': v$.item.Voornaam.$error }">
                <div class="form-label-group mb-2">
                    <input
                        type="text"
                        autocomplete="given-name"
                        class="form-label-group-input"
                        :id="`volwassene-${index}-Voornaam`"
                        :name="`volwassenen-${index}-Voornaam`"
                        v-model="item.Voornaam"
                        placeholder="Voornaam"
                        @blur="v$.item.Voornaam.$touch()"
                    />
                    <label :for="`volwassene-${index}-Voornaam`">Voornaam</label>
                    <div v-for="err in getFieldErrors('item.Voornaam')" :key="err" class="ic-error" role="alert">
                        <small class="text-danger">{{ err }}</small>
                    </div>
                </div>
            </div>

            <!-- Achternaam -->
            <div class="col-sm-6" :class="{ 'has-error': v$.item.Achternaam.$error }">
                <div class="form-label-group mb-2">
                    <input
                        type="text"
                        autocomplete="family-name"
                        class="form-label-group-input"
                        :id="`volwassene-${index}-Achternaam`"
                        :name="`volwassenen-${index}-Achternaam`"
                        v-model="item.Achternaam"
                        placeholder="Achternaam"
                        @blur="v$.item.Achternaam.$touch()"
                    />
                    <label :for="`volwassene-${index}-Achternaam`">Achternaam</label>
                    <div v-for="err in getFieldErrors('item.Achternaam')" :key="err" class="ic-error" role="alert">
                        <small class="text-danger">{{ err }}</small>
                    </div>
                </div>
            </div>

            <!-- Leeftijd -->
            <div class="col-sm-6" :class="{ 'has-error': v$.item.Leeftijd.$error }">
                <div class="form-label-group mb-2">
                    <input
                        type="number"
                        class="form-label-group-input"
                        :id="`volwassene-${index}-Leeftijd`"
                        :name="`volwassenen-${index}-Leeftijd`"
                        v-model.number="item.Leeftijd"
                        min="18"
                        placeholder="Leeftijd"
                        @blur="v$.item.Leeftijd.$touch()"
                    />
                    <label :for="`volwassene-${index}-Leeftijd`">Leeftijd</label>
                    <div v-for="err in getFieldErrors('item.Leeftijd')" :key="err" class="ic-error" role="alert">
                        <small class="text-danger">{{ err }}</small>
                    </div>
                </div>
            </div>

            <!-- Geslacht -->
            <div class="col-sm-6" :class="{ 'has-error': v$.item.Geslacht.$error }">
                <div class="form-label-group mb-2">
                    <select
                        class="form-label-group-input"
                        :id="`volwassene-${index}-Geslacht`"
                        :name="`volwassenen-${index}-Geslacht`"
                        v-model="item.Geslacht"
                        @blur="v$.item.Geslacht.$touch()"
                    >
                        <option value="" disabled hidden>Geslacht</option>
                        <option value="M">M</option>
                        <option value="V">V</option>
                        <option value="X">X</option>
                    </select>
                    <label :for="`volwassene-${index}-Geslacht`">Geslacht</label>
                    <div v-for="err in getFieldErrors('item.Geslacht')" :key="err" class="ic-error" role="alert">
                        <small class="text-danger">{{ err }}</small>
                    </div>
                </div>
            </div>

            <!-- Emailadres -->
            <div class="col-sm-12" :class="{ 'has-error': v$.item.Emailadres.$error }">
                <div class="form-label-group">
                    <input
                        type="email"
                        autocomplete="email"
                        class="form-label-group-input"
                        :id="`volwassene-${index}-Emailadres`"
                        :name="`volwassenen-${index}-Emailadres`"
                        v-model="item.Emailadres"
                        placeholder="naam@domein.nl"
                        @blur="v$.item.Emailadres.$touch()"
                    />
                    <label :for="`volwassene-${index}-Emailadres`">E-mailadres</label>
                    <div v-for="err in getFieldErrors('item.Emailadres')" :key="err" class="ic-error" role="alert">
                        <small class="text-danger">{{ err }}</small>
                    </div>
                </div>
            </div>

            <!-- Individuele vragen -->
            <div v-if="item.Vragen.length" class="col-sm-12 mt-2">
                <BestelVraagInput
                    v-for="vraagAntwoord in item.Vragen"
                    :key="vraagAntwoord.Id"
                    :vraag-antwoord="vraagAntwoord"
                    :prefix="`volwassene-${index}`"
                />
            </div>
        </div>
    </div>
</template>
